import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import * as XLSX from "xlsx";
import { FaRupeeSign, FaDollarSign, FaEuroSign } from "react-icons/fa";
import Axios from "../../../axios/Axios";
import { getUserPriceList, NODE_HOST_URL } from "../api/Api";
import styles from "./WhatsappPricing.module.css";

const WhatsappPricing = () => {
  const lStorage = JSON.parse(localStorage.getItem("login"));
  const userId = lStorage?.user?.userId;
  const userToken = lStorage?.token;
  const defaultCurrency = lStorage?.user?.country_currency || "INR";
  const [excelData, setExcelData] = useState([]);
  const [apiPriceList, setApiPriceList] = useState([]);
  const [apiEmailPrice, setApiEmailPrice] = useState(null);
  const [rcsList, setRcsList] = useState([]);
  const [channelExcel, setChannelExcel] = useState({
    sms: [],
    voice: [],
    email: [],
    rcs: []
  });
  const [activeTab, setActiveTab] = useState("WhatsApp");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const smsFallback = { INR: 0.20, USD: 0.0024, EUR: 0.0022 };
  const voiceFallback = { cost: { INR: 0.40, USD: 0.0048, EUR: 0.0044 }, pulse: 30 };
  const emailFallback = { INR: 0.15, USD: 0.0018, EUR: 0.00165 };

  const currencyIcon = { INR: <FaRupeeSign />, USD: <FaDollarSign />, EUR: <FaEuroSign /> };

  useEffect(() => {
    fetch("/channel-pricing.xlsx")
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const wb = XLSX.read(buffer);

        const mainSheet = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        // console.log("Excel loaded:", mainSheet);

        setChannelExcel({
          sms: mainSheet,
          voice: mainSheet,
          email: mainSheet,
          rcs: mainSheet
        });

      })
      .catch((e) => console.error("Channel Excel error:", e));
  }, []);

  useEffect(() => {
    fetch("/whatsapp-pricing.xlsx")
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const workbook = XLSX.read(buffer);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        setExcelData(XLSX.utils.sheet_to_json(sheet));
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        user_id: userId,
        token: userToken,
        method: "retrieve",
        keyword: "",
      };

      try {
        const result = await getUserPriceList(payload);
        if (result?.data?.success) {
          setApiPriceList(result.data.data || []);
          setApiEmailPrice(result.data.email_cost ?? null);
        }
      } catch (err) {
        console.error("getUserPriceList error:", err);
      }
    };

    fetchData();
  }, [userId, userToken]);

  useEffect(() => {
    const fetchRcs = async () => {
      try {
        const payload = {
          user_id: userId,
          token: userToken,
          method: "retrieve_price_personal",
        };

        const res = await Axios.post(`${NODE_HOST_URL}/rcs_setting`, payload);

        if (res?.data?.success && Array.isArray(res.data.data)) {
          setRcsList(res.data.data);
          return;
        }

        setRcsList([]);

      } catch (err) {
        console.error("RCS API error:", err);
        setRcsList([]);
      }
    };

    fetchRcs();
  }, [userId, userToken]);

  const getChannelExcelRow = (sheet = [], selectedCountry) => {
    if (!sheet || sheet.length === 0 || !selectedCountry) return null;

    const norm = (s) => ("" + (s ?? "")).trim().toLowerCase();

    const label = (selectedCountry.label || "").toString().trim().toLowerCase(); // "india (91)"
    const apiName = (selectedCountry.row?.country_name || "").toString().trim().toLowerCase(); // "india"
    const code = (selectedCountry.value || "").toString().trim(); // "91"

    let r = sheet.find((row) => norm(row.Country) === label);
    if (r) return r;

    if (code) {
      r = sheet.find((row) => (row.Country || "").includes("(" + code + ")"));
      if (r) return r;
      r = sheet.find((row) => `${row.CountryCode || row.Code || row.country_code || ""}`.toString().trim() === code);
      if (r) return r;
    }

    if (apiName) {
      r = sheet.find((row) => norm(row.Country).includes(apiName) || norm(row.Country) === apiName);
      if (r) return r;
    }

    const fallbackCols = ["CountryName", "Country_Name", "country", "name"];
    for (const col of fallbackCols) {
      r = sheet.find((row) => row[col] && norm(row[col]) === apiName);
      if (r) return r;
    }

    r = sheet.find((row) => (row.Country || "").toString().toLowerCase().includes(apiName));
    if (r) return r;

    return null;
  };



  const hasChannelPricing = (row, channel) => {
    switch (channel) {
      case "WhatsApp":
        return (
          Number(row.wp_conv_cost) > 0 ||
          Number(row.wp_sending_cost) > 0 ||
          Number(row.wp_util_cost) > 0 ||
          Number(row.wp_auth_cost) > 0
        );

      case "SMS":
        return Number(row.sms_cost) > 0;

      case "Voice":
        return Number(row.voice_cost) > 0;

      case "Email":
        return Number(apiEmailPrice) > 0;

      case "RCS":
        if (rcsList.length > 0) {
          return rcsList.some(
            (r) => `${r.country_code}` === `${row.country_code}`
          );
        }
        return true;
      default:
        return false;
    }
  };


  const countryOptions = useMemo(() => {
    let apiCountries = apiPriceList
      .filter((i) => i.country_name)
      .filter((i) => hasChannelPricing(i, activeTab))
      .map((i) => ({
        label: `${i.country_name} (${i.country_code})`,
        value: i.country_code,
        row: i,
      }));

    if (apiCountries.length > 0) return apiCountries;

    if (activeTab === "WhatsApp") {
      return excelData
        .filter((row) => {
          const service = Number(row[`SERVICE_COST_${currency}`]);
          const marketing = Number(row[`MARKETING_COST_${currency}`]);
          const utility = Number(row[`UTILITY_COST_${currency}`]);
          const auth = Number(row[`AUTHENTICATION_COST_${currency}`]);
          return service > 0 || marketing > 0 || utility > 0 || auth > 0;
        })
        .map((row, i) => ({
          label: row.Country,
          value: row.Country.match(/\((\d+)\)/)?.[1] || i,
          row,
        }));
    }

    return [];
  }, [apiPriceList, excelData, rcsList, activeTab, currency]);

  useEffect(() => {
    if (countryOptions.length === 0) return;

    const india = countryOptions.find(
      (c) =>
        c.label.toLowerCase().includes("india") ||
        c.value === 91 ||
        c.row?.country_name === "India"
    );

    if (india) {
      setSelectedCountry(india);
      setCurrency(india.row?.currency ?? defaultCurrency);
    } else {
      const first = countryOptions[0];
      setSelectedCountry(first);
      setCurrency(first.row?.currency ?? defaultCurrency);
    }
  }, [countryOptions, defaultCurrency]);

  const selectedApiRow = apiPriceList.find(
    (i) => `${i.country_code}` === `${selectedCountry?.value}`
  );

  const selectedExcelRow = excelData.find((r) => r.Country === selectedCountry?.label);

  const getPrice = (apiVal, excelVal, fallback) => {
    if (apiVal !== null && apiVal !== undefined) return apiVal;
    if (excelVal !== null && excelVal !== undefined && excelVal !== "n/a") return excelVal;
    return fallback;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined) return "n/a";
    const num = parseFloat(value);
    if (isNaN(num)) return "n/a";
    return (
      <span className={styles.priceCell}>
        {currencyIcon[currency]} {num.toFixed(3)}
      </span>
    );
  };

  const wa = {
    service: getPrice(
      selectedApiRow?.wp_conv_cost,
      selectedExcelRow?.[`SERVICE_COST_${currency}`],
      null
    ),
    marketing: getPrice(
      selectedApiRow?.wp_sending_cost,
      selectedExcelRow?.[`MARKETING_COST_${currency}`],
      null
    ),
    utility: getPrice(
      selectedApiRow?.wp_util_cost,
      selectedExcelRow?.[`UTILITY_COST_${currency}`],
      null
    ),
    auth: getPrice(
      selectedApiRow?.wp_auth_cost,
      selectedExcelRow?.[`AUTHENTICATION_COST_${currency}`],
      null
    ),
  };

  const smsExcelRow = getChannelExcelRow(channelExcel.sms, selectedCountry);


  const sms = getPrice(
    selectedApiRow?.sms_cost,
    smsExcelRow?.["SMS Cost (INR)"],
    smsFallback[currency]
  );


  const voiceExcelRow = getChannelExcelRow(channelExcel.voice, selectedCountry);


  const voice = {
    cost: getPrice(
      selectedApiRow?.voice_cost,
      voiceExcelRow?.["Voice Cost (INR)"],
      voiceFallback.cost[currency]
    ),
    pulse: getPrice(
      selectedApiRow?.voice_pulse,
      voiceExcelRow?.["Voice Pulse"],
      voiceFallback.pulse
    ),
  };


  const emailExcelRow = getChannelExcelRow(channelExcel.email, selectedCountry);


  const email = getPrice(
    apiEmailPrice,
    emailExcelRow?.["Email Cost (INR)"],
    emailFallback[currency]
  );

  const rcsExcelRow = channelExcel.rcs.find(row =>
    row.Country?.toLowerCase().trim() === selectedCountry?.label?.toLowerCase().trim()
  );

  const selectedRcsRow =
    rcsList.length > 0
      ? rcsList.find(
        (r) => `${r.country_code}` === `${selectedCountry?.value}`
      )
      : null;

  const rcs = {
    a2p_single:
      selectedRcsRow?.a2p_single_sms_cost ??
      rcsExcelRow?.["A2P Single Cost"] ??
      null,

    a2p_conv:
      selectedRcsRow?.a2p_conv_cost ??
      rcsExcelRow?.["A2P Conversation Cost"] ??
      null,

    p2a_conv:
      selectedRcsRow?.p2a_conv_cost ??
      rcsExcelRow?.["P2A Conversation Cost"] ??
      null,

    basic:
      selectedRcsRow?.basic_sms_cost ??
      rcsExcelRow?.["Basic Cost"] ??
      null,
  };

  const tabs = ["WhatsApp", "SMS", "Email", "RCS", "Voice"];

  return (
    <div className={`container ${styles.wrapper}`}>

      <div className={`d-flex mb-4 ${styles.tabContainer}`}>
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`${styles.tabButton} ${activeTab === t ? styles.activeTab : ""}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="row">
        <div className="col-12 col-sm-8 col-md-5 col-lg-4">
          <Select
            value={selectedCountry}
            options={countryOptions}
            onChange={(v) => {
              setSelectedCountry(v);
              const newCurrency = v?.row?.currency ?? defaultCurrency;
              if (newCurrency) setCurrency(newCurrency.toUpperCase());
            }}
          />
        </div>
      </div>
      <div className={styles.pricingCard}>
        {activeTab === "WhatsApp" && (
          <>
            <div className={styles.rowBetween}>
              <h5 className={styles.cardTitle}>WhatsApp Pricing</h5>
              <Link className={styles.addCreditBtn} to="/dashboard/transaction-payment">
                Add Credit
              </Link>
            </div>

            <div className={styles.categoryBox}>
              <p className={styles.categoryHeading}>User-Initiated</p>
              <div className={styles.rowBetween}>
                <div>
                  <p className={styles.subCategory}>Service</p>
                  <p className={styles.description}>
                    Conversations initiated by users for service queries.
                  </p>
                </div>
                <p className={styles.price}>{formatPrice(wa.service)}</p>
              </div>
            </div>

            <div className={styles.categoryBox}>
              <p className={styles.categoryHeading}>Business-Initiated</p>

              <div className={styles.rowBetween}>
                <div>
                  <p className={styles.subCategory}>Marketing</p>
                  <p className={styles.description}>
                    Promotional offers, updates, or new launches.
                  </p>
                </div>
                <p className={styles.price}>{formatPrice(wa.marketing)}</p>
              </div>

              <div className="d-flex justify-content-between mt-2">
                <div>
                  <p className={styles.subCategory}>Utility</p>
                  <p className={styles.description}>
                    Transactional messages like order updates or receipts.
                  </p>
                </div>
                <p className={styles.price}>{formatPrice(wa.utility)}</p>
              </div>

              <div className="d-flex justify-content-between mt-2">
                <div>
                  <p className={styles.subCategory}>Authentication</p>
                  <p className={styles.description}>
                    One-time passcodes for account verification.
                  </p>
                </div>
                <p className={styles.price}>{formatPrice(wa.auth)}</p>
              </div>
            </div>
          </>
        )}

        {activeTab === "SMS" && (
          <div>
            <div className={styles.rowBetween}>
              <h5>SMS Pricing</h5>
              <Link className={styles.addCreditBtn} to="/dashboard/transaction-payment">
                Add Credit
              </Link>
            </div>

            <div className={styles.categoryBox}>
              <div className={styles.rowBetween}>
                <p className={styles.subCategory}>SMS Cost</p>
                <p className={styles.price}>{formatPrice(sms)}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Email" && (
          <div>
            <div className={styles.rowBetween}>
              <h5>Email Pricing</h5>
              <Link className={styles.addCreditBtn} to="/dashboard/transaction-payment">
                Add Credit
              </Link>
            </div>

            <div className={styles.categoryBox}>
              <div className={styles.rowBetween}>
                <p className={styles.subCategory}>Email Cost</p>
                <p className={styles.price}>{formatPrice(email)}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "RCS" && (
          <div>
            <div className={styles.rowBetween}>
              <h5>RCS Pricing</h5>
              <Link className={styles.addCreditBtn} to="/dashboard/transaction-payment">
                Add Credit
              </Link>
            </div>

            <div className={styles.categoryBox}>
              <div className={styles.rowBetween}>
                <p className={styles.subCategory}>A2P Single Cost</p>
                <p className={styles.price}>{formatPrice(rcs.a2p_single)}</p>
              </div>

              <div className="d-flex justify-content-between mt-2">
                <p className={styles.subCategory}>A2P Conversation Cost</p>
                <p className={styles.price}>{formatPrice(rcs.a2p_conv)}</p>
              </div>

              <div className="d-flex justify-content-between mt-2">
                <p className={styles.subCategory}>P2A Conversation Cost</p>
                <p className={styles.price}>{formatPrice(rcs.p2a_conv)}</p>
              </div>

              <div className="d-flex justify-content-between mt-2">
                <p className={styles.subCategory}>Basic Cost</p>
                <p className={styles.price}>{formatPrice(rcs.basic)}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Voice" && (
          <div>
            <div className={styles.rowBetween}>
              <h5>Voice Pricing</h5>
              <Link className={styles.addCreditBtn} to="/dashboard/transaction-payment">
                Add Credit
              </Link>
            </div>

            <div className={styles.categoryBox}>
              <div className={styles.rowBetween}>
                <p className={styles.subCategory}>Voice Cost</p>
                <p className={styles.price}>{formatPrice(voice.cost)}</p>
              </div>

              <div className="d-flex justify-content-between mt-2">
                <p className={styles.subCategory}>Pulse</p>
                <p className={styles.price}>{voice.pulse} sec</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappPricing;
